import React, { useState, useEffect } from 'react';
import { auth } from '../firebase/config';
import { getFeatures, isJigLocalOn, isJigWebOn } from '../utils/features';
import { useJigConsent } from './common/JigConsentGate';
import { LAB_PLANS } from '../utils/billingPolicy';
import JigUsageBar, { useJigUsage } from './common/JigUsageBar';

// 🧩 구독 화면 — 기공소 플랜 3종 표(지그 월 한도) + 이번 달 사용량 막대 (2026-10-30 구독 플랜제, 계획_구독플랜제 §1·§2 대표 결정)
//   종전(10-23~10-28) 지그 사용 구간표(JIG_TIERS)·'현재 구간' 배지는 플랜제로 대체. 사용량은 서버 callable getJigUsage(개수·한도만, 환자정보 없음).
//   showUsage=false: 같은 페이지(AppSubscription 플랜 카드)에 막대가 이미 있을 때 표만 보인다.
//   2026-10-25 대표: 오른쪽에 「구독해야 쓸 수 있는 기능」(거래명세서·제작 공정·예상 매출 등)을 나란히 보여 구독료와 지그 요금의 관계를 알 수 있게.
//   목록 근거 = utils/billingPolicy ACCESS_ALLOW.lab_limited(미구독 기공소 열림 목록) + 약관 v8.0 초안 제4조 5의2.
// 노출 = 기공소 && (jigLocal || jigWeb) — 지그 메뉴와 같은 게이트. 안내 표시 전용(과금 아님).
// ⚠️ 환자정보 경계: 이번 달 개수는 jigResults(치식) 기준이라 지그 첫 이용 동의 전에는 조회하지 않는다(Jig.js 와 동일 fail-closed).
//    여기선 개수만 계산하고 환자·치과·치식은 화면에 내보내지 않는다.
// 미구독(lab_limited)에서 잠기는 기능 — billingPolicy.ACCESS_ALLOW.lab_limited 에 없는 경로들. 단어만(대표 10-25).
const SUBSCRIBER_ONLY = [
    { icon: '🧾', name: '거래명세서 작성·발행', sub: '이미 만든 명세서 열람·인쇄는 무료' },
    { icon: '🏭', name: '제작 공정(기공 현황)', sub: '의뢰서 확인 → 디자인 완료 → 포장 완료' },
    { icon: '📈', name: '예상 매출', sub: '이번 달 수집분 기준' },
    { icon: '🧮', name: '세금계산서 작성', sub: '기존 목록 열람은 무료' },
    { icon: '💻', name: '덴클 프로그램 수집', sub: 'CAD 폴더 자동 수집' },
    { icon: '🧩', name: '지그 자동 생성', sub: '체험 1개월 뒤에는 구독이 필요 · 플랜별 월 한도' },   // 3D 모델은 허용 기공소만(2026-10-30)
];

export default function JigUsagePlan({ user, showUsage = true }) {
    const labId = user?.companyId || auth.currentUser?.uid;
    const isLab = user?.businessType === 'lab' || user?.companyBusinessType === 'lab';
    const consent = useJigConsent();
    const [jigOn, setJigOn] = useState(false);
    // 사용량 조회는 종전과 같이 지그 첫 이용 동의 뒤에만 (fail-closed 유지 — 동의 전에는 서버 호출 0)
    const jigUsage = useJigUsage(showUsage && jigOn && consent.agreed === true && !!labId, [labId]);

    useEffect(() => {
        if (!isLab) return undefined;
        let alive = true;
        getFeatures()
            .then((f) => {
                if (!alive) return;
                const email = auth.currentUser?.email;
                setJigOn(isJigLocalOn(f, user, email) || isJigWebOn(f, user, email));
            })
            .catch((e) => { console.error('[지그 요금] 기능 설정 조회 실패(카드 숨김):', e?.message || e); });
        return () => { alive = false; };
    }, [isLab, user]);

    if (!isLab || !jigOn) return null;

    const currentCode = jigUsage.usage?.planCode || null;

    return (
        <div style={styles.section}>
          <div style={styles.twoCol}>
          <div style={styles.col}>
            <h2 style={styles.sectionTitle}>
                <span style={{ fontSize: '20px' }}>{'🧩'}</span>
                {'플랜별 지그 한도'}
            </h2>
            <p style={styles.sectionDesc}>{'지그는 커스텀 치아 1개를 1개로 셉니다. 실패한 생성은 세지 않습니다. 세 플랜 모두 전 기능이 열리고, 차이는 지그 월 한도뿐입니다.'}</p>

            {showUsage && (
                <div style={styles.usageBox}>
                    {/* 동의 조회 실패는 '동의하세요'가 아니라 실패로 안내(이미 동의한 사람 오안내 방지 — 검토자 P2) */}
                    {!consent.loading && consent.error ? (
                        <p style={styles.error}>{'동의 상태를 확인하지 못해 이번 달 사용량을 표시하지 못했습니다. 잠시 후 다시 열어 주세요.'}</p>
                    ) : !consent.loading && !consent.agreed ? (
                        <p style={styles.hint}>{'지그 화면에서 첫 이용 동의를 하시면 이번 달 사용량이 표시됩니다.'}</p>
                    ) : (
                        <JigUsageBar usage={jigUsage.usage} error={jigUsage.error} loading={jigUsage.loading || consent.loading} />
                    )}
                </div>
            )}

            {/* 2026-10-30 대표 확정 플랜표 — 플랜·월 구독료(부가세 포함)·지그 한도·기능 */}
            <div style={styles.tableWrap}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>{'플랜'}</th>
                            <th style={styles.th}>{'월 구독료'}</th>
                            <th style={styles.th}>{'지그 한도(월)'}</th>
                            <th style={styles.th}>{'기능'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {LAB_PLANS.map((p) => {
                            const active = p.code === currentCode;
                            return (
                                <tr key={p.code} style={active ? styles.trActive : undefined}>
                                    <td style={styles.td}>
                                        {p.label}
                                        {active && <span style={styles.hereTag}>{'현재 플랜'}</span>}
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: 800, color: active ? '#4338ca' : '#0f172a' }}>{`${p.amount.toLocaleString('ko-KR')}원`}</td>
                                    <td style={styles.td}>{`${p.jigLimit.toLocaleString('ko-KR')}개`}</td>
                                    <td style={styles.td}>{'전 기능'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <p style={styles.hint}>{'부가세 포함 · 한도를 넘어도 생성은 멈추지 않습니다. 90%에 닿으면 미리 알려드리고, 한도를 넘기면 다음 달부터 다음 플랜으로 전환됩니다(프리미엄 초과는 별도 안내).'}</p>
            {/* 법적보안 확인 대기(2026-10-30): 플랜제 적용 시작일·약관 v8.0 개정 공고는 대표 planV8At 입력·약관 게시 후 */}
            <p style={styles.note}>{'플랜 변경·적용 시작일과 세부 조건은 이용약관(지그 사용료 조)에 따릅니다.'}</p>
          </div>
          {/* 오른쪽: 구독해야 쓸 수 있는 기능 (대표 10-25) — 단어만 */}
          <div style={styles.colSide}>
            <h2 style={styles.sectionTitle}>
                <span style={{ fontSize: '20px' }}>{'🔒'}</span>
                {'구독해야 쓸 수 있는 기능'}
            </h2>
            <p style={styles.sectionDesc}>{'플랜 구독(베이직 55,000원부터)에 포함. 지그 한도는 플랜별.'}</p>
            <div style={styles.lockList}>
                {SUBSCRIBER_ONLY.map((f) => (
                    <div key={f.name} style={styles.lockItem}>
                        <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{f.icon}</span>
                        <div>
                            <div style={styles.lockName}>{f.name}</div>
                            <div style={styles.lockSub}>{f.sub}</div>
                        </div>
                    </div>
                ))}
            </div>
          </div>
          </div>
        </div>
    );
}

const styles = {
    // section·sectionTitle·sectionDesc = AppSubscription 섹션과 같은 모양
    section: { backgroundColor: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '24px', border: '1px solid #e2e8f0' },
    twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 },
    col: { minWidth: 0 },
    colSide: { minWidth: 0, borderLeft: '1px solid #e2e8f0', paddingLeft: 24 },
    lockList: { display: 'flex', flexDirection: 'column', gap: 10 },
    lockItem: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 10, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' },
    lockName: { fontSize: 14, fontWeight: 700, color: '#0f172a' },
    lockSub: { fontSize: 12, color: '#64748b', marginTop: 2 },
    sectionTitle: { display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' },
    sectionDesc: { margin: '0 0 16px 0', fontSize: '14px', color: '#64748b' },
    usageBox: { padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 14 },
    hint: { fontSize: 13, color: '#64748b', margin: '0 0 12px 0' },
    error: { fontSize: 13, color: '#dc2626', margin: '0 0 12px 0' },
    tableWrap: { overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 420 },
    th: { textAlign: 'left', padding: '10px 12px', backgroundColor: '#f8fafc', color: '#64748b', fontWeight: 700, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', whiteSpace: 'nowrap' },
    trActive: { backgroundColor: '#eef2ff' },
    hereTag: {
        marginLeft: 8, fontSize: 11, fontWeight: 800, padding: '2px 8px',
        borderRadius: 6, backgroundColor: '#6366f1', color: '#fff', verticalAlign: 'middle',
    },
    note: { fontSize: 12, color: '#64748b', margin: '12px 0 0 0' },
};
